import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import RightPanel from '../../../RightPanel'
import Icon from '../../../../atoms/Icon'
import FlexRow from '../../../../molecules/FlexRow'
import { getTaskStatus } from '../shared/redtailUIHelpers'
import { LinkedContactRow } from '../shared/LinkedContactDisplay'
import ActivityDetailModal from '../shared/ActivityDetailModal'
import useCategoryOptions from '../redtailList/useCategoryOptions'

const METRIC_INVALIDATE_KEYS = [
  [QUERY_KEYS.redtailDomainQuery, 'metric-preset'],
  [QUERY_KEYS.redtailDomainQuery, 'list-preset']
]

function TaskRow ({ activity, onClick }) {
  const status = getTaskStatus(activity)
  const dueFormatted = activity.end_date
    ? dayjs(activity.end_date).format('MMM D, YYYY')
    : null

  return (
    <div style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
      <div
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 1.5rem',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)'
        }}
        >
          <Icon
            name={status === 'completed' ? 'checkCircle' : status === 'overdue' ? 'clock' : 'radioUnchecked'}
            customSize='18px'
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            <LinkedContactRow contacts={activity.linked_contacts} />
          </div>
          <div style={{
            fontSize: 'var(--font-size-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {activity.subject || 'Untitled Task'}
          </div>
          {dueFormatted ? (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
              marginTop: '2px'
            }}
            >
              {status === 'completed' ? 'Completed' : `Due ${dueFormatted}`}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

TaskRow.propTypes = {
  activity: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function RedtailTaskPanel ({ open, onClose, items, title, config, members }) {
  const { clientId } = useAppContext()
  const categoryOptions = useCategoryOptions(clientId)
  const [selectedActivity, setSelectedActivity] = useState(null)

  const enableEdit = config?.enableEdit ?? false
  const formMode = config?.formMode ?? 'simple'

  const sorted = useMemo(() => {
    if (!items?.length) return []
    return [...items].sort((a, b) => {
      const da = a.end_date ? dayjs(a.end_date).valueOf() : Infinity
      const db = b.end_date ? dayjs(b.end_date).valueOf() : Infinity
      return da - db
    })
  }, [items])

  const handleCloseDetail = useCallback(() => setSelectedActivity(null), [])

  return (
    <RightPanel
      open={open}
      toggleOpen={onClose}
      title={title || 'Tasks'}
      showCloseButton
    >
      <div style={{ paddingBottom: '1.5rem' }}>
        {!sorted.length
          ? (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '120px',
                padding: '0 1.5rem',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              No current tasks
            </FlexRow>
          )
          : sorted.map(activity => (
            <TaskRow
              key={activity.id}
              activity={activity}
              onClick={() => setSelectedActivity(activity)}
            />
          ))}
      </div>

      <ActivityDetailModal
        open={!!selectedActivity}
        onClose={handleCloseDetail}
        activity={selectedActivity}
        config={config}
        members={members}
        enableEdit={enableEdit}
        formMode={formMode}
        invalidateQueryKeys={METRIC_INVALIDATE_KEYS}
        categoryOptions={categoryOptions}
      />
    </RightPanel>
  )
}

RedtailTaskPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  items: PropTypes.array,
  title: PropTypes.string,
  config: PropTypes.object,
  members: PropTypes.array
}

export default RedtailTaskPanel
